import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { NotificationList } from 'modules/notification/NotificationList';
import { FC, RefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DropdownWrapper } from 'shared/components/DropdownWrapper';
import { LinkButton } from 'shared/components/LinkButton';
import { useSitemap } from 'shared/hooks/useSitemap';
import { NotificationTypeEnum } from 'types/enums/notification-type.enum';

interface NotificationDropdownProps {
  open: boolean;
  onClose: () => void;
  buttonRef: RefObject<HTMLButtonElement | null>;
}

export const NotificationDropdown: FC<NotificationDropdownProps> = ({
  open,
  onClose,
  buttonRef,
}) => {
  const { t } = useTranslation();
  const { notifications, loading, markAsViewedByType } = useNotifications();
  const sitemap = useSitemap();
  const { pathname } = useLocation();

  const handleClose = useCallback(() => {
    onClose();
    markAsViewedByType(NotificationTypeEnum.SavedOfferExpired);
  }, [onClose, markAsViewedByType]);

  useEffect(() => {
    if (open) {
      handleClose();
    }
  }, [pathname]);

  if (!open || !buttonRef) {
    return null;
  }

  return (
    <DropdownWrapper
      className="!left-auto !right-3 hidden md:block"
      topOffset={14}
      onClose={handleClose}
      position="fixed"
      targetRef={buttonRef}
      setWidth={false}
    >
      <div className="w-screen max-w-[495px] rounded-lg bg-white pt-6 shadow-lg">
        <h3 className="mb-7 ml-6 text-lg font-bold">{t('pageTitles:notifications')}</h3>
        <div className="max-h-[500px] overflow-auto">
          <NotificationList
            notifications={notifications}
            loading={loading}
            itemVariant="short"
            onRedirect={handleClose}
          />
        </div>
        <div className="flex justify-end p-4 pt-8">
          <LinkButton to={sitemap.notifications} name="showAllNotificationsRedirectButton">
            {t('notification:showAllNotifications')}
          </LinkButton>
        </div>
      </div>
    </DropdownWrapper>
  );
};
